import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';
import { useUnits, useProjects } from '../../../../core/api';
import clsx from 'clsx';
import { ApiUnit, ApiProject } from '../../../../core/api/types';
import { RemoteImage } from '../../../../core/components/RemoteImage';

interface UnitSelectorProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

export const UnitSelector: React.FC<UnitSelectorProps> = ({ selectedIds, onChange }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterProjectId, setFilterProjectId] = useState<string>('');

    // Fetch projects for filter
    const { data: projects = [] } = useProjects({ limit: 100 });

    // Fetch all units (limit 100 for now)
    // In a real scenario, this should be paginated or server-side searched
    const { data: units = [], isLoading } = useUnits({ limit: 100 });

    const toggleUnit = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(uid => uid !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const filteredUnits = (units as any as unknown as ApiUnit[]).filter(u => {
        const matchesSearch =
            (u.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (u.slug?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        const matchesProject = filterProjectId ? u.project?.id === filterProjectId : true;

        return matchesSearch && matchesProject;
    });

    // Sort: Selected first, then by title
    const sortedUnits = [...filteredUnits].sort((a, b) => {
        const aSelected = selectedIds.includes(a.id);
        const bSelected = selectedIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return (a.title || '').localeCompare(b.title || '');
    });

    if (isLoading) return <div className="p-4 text-center text-slate-500">Loading units...</div>;

    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder={t('common.search', 'Search units...')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                </div>
                <select
                    className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 max-w-[150px]"
                    value={filterProjectId}
                    onChange={e => setFilterProjectId(e.target.value)}
                >
                    <option value="">All Projects</option>
                    {(projects as ApiProject[]).map(p => (
                        <option key={p.id} value={p.id}>{p.title || p.slug}</option>
                    ))}
                </select>
            </div>

            <div className="border rounded-lg max-h-60 overflow-y-auto">
                {sortedUnits.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">No units found.</div>
                ) : (
                    <div className="divide-y text-slate-900">
                        {sortedUnits.map(unit => {
                            const isSelected = selectedIds.includes(unit.id);
                            return (
                                <div
                                    key={unit.id}
                                    onClick={() => toggleUnit(unit.id)}
                                    className={clsx(
                                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors",
                                        isSelected && "bg-primary/5"
                                    )}
                                >
                                    <div className={clsx(
                                        "w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                        isSelected ? "bg-primary border-primary text-white" : "border-slate-300 bg-white"
                                    )}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {unit.title || unit.slug}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">
                                            {unit.project?.title || 'No Project'} • {unit.price ? `${unit.price.toLocaleString()} EGP` : 'Price on request'}
                                        </p>
                                    </div>
                                    {unit.image && (
                                        <RemoteImage
                                            src={unit.image}
                                            alt=""
                                            className="w-10 h-10 rounded object-cover bg-slate-100"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2 text-slate-900">
                {selectedIds.map(id => {
                    // Try to find in current list (might be filtered out)
                    // If not found, we might need to fetch it or just show ID if we can't find details
                    // For now, assuming they are in the list or we don't care about rendering missing ones perfectly immediately
                    const unit = (units as any as unknown as ApiUnit[]).find(u => u.id === id);
                    if (!unit) return null;
                    return (
                        <div key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 border rounded-full text-xs">
                            <span className="max-w-[100px] truncate">{unit.title || unit.slug}</span>
                            <button
                                onClick={() => toggleUnit(id)}
                                className="hover:text-red-500 p-0.5"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
